import type React from 'react';
import {Navigate, type RouteObject} from 'react-router';
import type {ReportRoute} from '../../../../screens/stations/StationDetail/StationAnalysis/reportAccess';

type ReportScreen = {Component: React.ComponentType};

/**
 * Every analysis report screen, by its route under `analysis/`. Keyed by
 * `ReportRoute`, the keys of `REPORT_PLANS`, so a report cannot be routed
 * without a plan, and every one of them is routed through
 * `withReportAccess` (FE #99): the card in ReportList used to be the only
 * gate, and a typed URL skipped it.
 */
const REPORT_SCREENS: Record<ReportRoute, () => Promise<ReportScreen>> = {
  records: () =>
    import('../../../../screens/stations/StationDetail/StationAnalysis/RecordsReport'),
  'on-this-day': () =>
    import('../../../../screens/stations/StationDetail/StationAnalysis/OnThisDayReport'),
  'monthly-outdoor': () =>
    import('../../../../screens/stations/StationDetail/StationAnalysis/MonthlyOutdoorReport'),
  'annual-outdoor': () =>
    import('../../../../screens/stations/StationDetail/StationAnalysis/AnnualOutdoorReport'),
  'annual-comparison': () =>
    import('../../../../screens/stations/StationDetail/StationAnalysis/AnnualComparisonReport'),
  export: () =>
    import('../../../../screens/stations/StationDetail/StationAnalysis/DataExportReport'),
  'station-comparison': () =>
    import('../../../../screens/stations/StationDetail/StationAnalysis/StationComparisonReport'),
  'temperature-feel': () =>
    import('../../../../screens/stations/StationDetail/StationAnalysis/TemperatureFeelReport'),
  'dew-point': () =>
    import('../../../../screens/stations/StationDetail/StationAnalysis/DewPointReport'),
  'temperature-comparison': () =>
    import('../../../../screens/stations/StationDetail/StationAnalysis/TemperatureComparisonReport'),
  'wind-monthly': () =>
    import('../../../../screens/stations/StationDetail/StationAnalysis/MonthlyWindReport'),
  'wind-run': () =>
    import('../../../../screens/stations/StationDetail/StationAnalysis/WindRunReport'),
  'rain-annual': () =>
    import('../../../../screens/stations/StationDetail/StationAnalysis/AnnualRainReport'),
  'rain-comparison': () =>
    import('../../../../screens/stations/StationDetail/StationAnalysis/RainComparisonReport'),
  'solar-monthly': () =>
    import('../../../../screens/stations/StationDetail/StationAnalysis/MonthlySolarReport'),
  'solar-annual': () =>
    import('../../../../screens/stations/StationDetail/StationAnalysis/AnnualSolarReport'),
  'grassland-temperature': () =>
    import('../../../../screens/stations/StationDetail/StationAnalysis/GrasslandTemperatureReport'),
  'annual-water-balance': () =>
    import('../../../../screens/stations/StationDetail/StationAnalysis/AnnualWaterBalanceReport'),
};

const reportRoutes = (Object.keys(REPORT_SCREENS) as ReportRoute[]).map(
  (route) => ({
    path: `analysis/${route}`,
    // Marks the route as gated; stationDetailRoutes.test checks every
    // `analysis/*` route carries it.
    handle: {reportAccess: route},
    lazy: async () => {
      // The guard is loaded with the report, not with this route table:
      // it pulls in PremiumDialog, which the entry chunk must not carry.
      const [screen, {default: withReportAccess}] = await Promise.all([
        REPORT_SCREENS[route](),
        import('./withReportAccess'),
      ]);
      return {Component: withReportAccess(route, screen.Component)};
    },
  }),
);

export const stationDetailRoutes: RouteObject[] = [
  {
    path: '',
    lazy: () =>
      import('../../../../screens/stations/StationDetail/StationDashboard'),
  },
  {
    path: 'forecast',
    lazy: () =>
      import('../../../../screens/stations/StationDetail/StationForecast'),
  },
  {
    path: 'forecast-comparison',
    lazy: () =>
      import('../../../../screens/stations/StationDetail/StationForecastComparison'),
  },
  {
    path: 'history',
    lazy: () =>
      import('../../../../screens/stations/StationDetail/StationHistory'),
  },
  {
    path: 'analysis',
    lazy: () =>
      import('../../../../screens/stations/StationDetail/StationAnalysis'),
  },
  ...reportRoutes,
  {
    path: 'sun',
    lazy: () => import('../../../../screens/stations/StationDetail/SunDetail'),
  },
  {
    path: 'moon',
    lazy: () => import('../../../../screens/stations/StationDetail/MoonDetail'),
  },
  {
    path: 'warnings',
    lazy: () =>
      import('../../../../screens/stations/StationDetail/WeatherWarnings'),
  },
  {
    // The URL carries the selected warning, so a detail can be linked to.
    path: 'warnings/:warningId',
    lazy: () =>
      import('../../../../screens/stations/StationDetail/WeatherWarnings'),
  },
  {
    path: 'sensors/:sensorType',
    lazy: () =>
      import('../../../../screens/stations/StationDetail/SensorCharts'),
  },
  {
    path: 'sensors/:sensorType/:chartType',
    lazy: () =>
      import('../../../../screens/stations/StationDetail/SensorCharts/SensorChart'),
  },
  {
    path: '*',
    element: <Navigate to={''} replace />,
  },
];
